import { motion } from 'framer-motion';
import styled, { css, keyframes } from 'styled-components';

import { type ActivityPanelVariants } from './ActivityList.types';

export const Wrapper = styled.div`
    width: 100%;

    display: flex;
    flex-direction: column;
    flex-grow: 1;
`;

export const ListWrapper = styled.div`
    overflow: scroll;
    position: relative;
    height: 300px;
    padding-top: 10px;
    padding-bottom: 50px;

    flex-grow: 1;

    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    mask-image: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0) 0,
        #ffffff 20px,
        #ffffff 70%,
        rgba(255, 255, 255, 0) 100%
    );
`;

export const ListContainer = styled.div`
    position: relative;
`;

export const ListItem = styled.div`
    width: calc(100% - 60px);
`;

export const ListItemSkeleton = styled(motion.div)`
    width: 100%;
    height: 60px;
    background: linear-gradient(90deg, #2d2b3d 0%, #151327 100%);
    border-radius: 8px;
    flex-shrink: 0;
    opacity: 0.3;
    pointer-events: none;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        height: 80px;
    }
`;

export const SkeletonList = styled.div`
    padding: 10px 30px 60px 30px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const Content = styled.div<{ $variant?: ActivityPanelVariants }>`
    width: 100%;
    display: grid;
    gap: 10px;

    padding: 20px 20px 0 20px;

    grid-template-columns: 1fr min-content;

    grid-template-areas:
        'title liveStatus'
        'activityToggle activeWallets'
        'activityFilters activityFilters';

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: 0 30px;

        grid-template-areas:
            'title liveStatus'
            'description description'
            'activityToggle activityToggle'
            'activityFilters activityFilters';
    }
`;

export const Title = styled.div`
    grid-area: title;
    font-family: 'Alliance No.1';
    font-weight: 700;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: -0.02em;
    color: #ffffff;

    display: flex;
    align-items: center;
    gap: 9px;

    white-space: nowrap;

    svg {
        opacity: 0.5;
        width: 16px;
        height: 16px;

        &:hover {
            opacity: 1;
        }
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 20px;
    }
`;

export const ActiveWallets = styled.div`
    display: flex;
    gap: 3px;
    align-items: center;
    grid-area: activeWallets;

    font-weight: 700;
    font-size: 12px;
    line-height: 100%;
    letter-spacing: -0.02em;
    text-transform: lowercase;

    color: #ffffff;
    top: 1px;
    position: relative;

    span {
        font-size: 11px;
        line-height: 100%;
        opacity: 0.7;
        white-space: nowrap;
        position: relative;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 14px;

        span {
            top: 1px;
            font-size: 12px;
        }
    }
`;

export const LiveStatus = styled.div<{ $status: 'active' | 'paused' | 'fetching' }>`
    grid-area: liveStatus;

    font-family: 'Alliance No.1';
    font-style: normal;
    font-weight: 700;
    font-size: 12px;
    line-height: 13px;
    letter-spacing: -0.02em;
    color: #ffffff;

    width: 75px;
    height: 25px;
    background: #ff004c;
    border-radius: 6px;

    display: flex;
    align-items: center;
    justify-content: center;

    justify-self: end;

    transition: all 0.3s ease-in-out;

    ${({ $status }) => {
        if ($status === 'fetching') {
            return css`
                background: linear-gradient(94.36deg, #ff7222 6.76%, #ff8a00 67.4%);
            `;
        }
    }}
`;

export const Description = styled.div`
    grid-area: description;

    font-family: 'Alliance No.1';
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 17px;
    letter-spacing: -0.03em;
    opacity: 0.7;

    display: none;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        display: block;
    }
`;

export const ActivityToggle = styled.div`
    grid-area: activityToggle;

    color: rgba(255, 255, 255, 0.5);

    span {
        font-family: 'Alliance No.1';
        font-style: normal;
        font-weight: 500;
        font-size: 12px;
        line-height: 130%;
        letter-spacing: -0.03em;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        span {
            font-size: 14px;
        }
    }
`;

const activityDotPulse = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(255, 0, 77, 0.6); }
    100% { box-shadow: 0 0 0 8px rgba(255, 0, 77, 0); }
`;

const activityDotPulseFetching = keyframes`
    0% { box-shadow: 0 0 0 0  rgba(255, 138, 50, 0.6) }
    100% { box-shadow: 0 0 0 8px rgba(255, 0, 77, 0); }
`;

export const ActivityDot = styled.div<{ $isFetching: boolean }>`
    background: rgba(255, 0, 77, 1);
    width: 6px;
    height: 6px;
    border-radius: 100%;
    position: relative;

    margin-right: 8px;

    animation-name: ${activityDotPulse};
    animation-duration: 3s;
    animation-iteration-count: infinite;

    ${({ $isFetching }) =>
        $isFetching &&
        css`
            background: linear-gradient(94.36deg, #ff7222 6.76%, #ff8a00 67.4%);
            animation-name: ${activityDotPulseFetching};
        `}

    &::before,
    &::after {
        content: '';
        animation-name: ${activityDotPulse};
        ${({ $isFetching }) =>
            $isFetching &&
            css`
                animation-name: ${activityDotPulseFetching};
            `}
        animation-duration: 3s;
        animation-iteration-count: infinite;
        animation-delay: 1s;

        width: 6px;
        height: 6px;

        position: absolute;
        top: 0;
        left: 0;
        border-radius: 100%;

        background-color: transparent;
    }

    &::after {
        animation-delay: 2s;
    }
`;

export const StatusWrapper = styled.div`
    display: flex;
    gap: 10px;
`;

export const StyledImage = styled.img`
    width: 70px !important;
`;

export const EmptyStateWrapper = styled.div`
    height: 300px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 20px;
`;

export const EmptyCopy = styled.span`
    font-weight: 700;
    font-size: 22px;
`;
